import { Link } from "@inertiajs/react"

interface Option {
  id: number
  name: string
}

interface LogFormData {
  header: string
  log: string
  notes: string
  log_date: string
  scheduled_date: string | null
  scheduled_notes: string
  log_type_id: number | null
  customer_location_id: number | null
  customer_user_id: number | null
  user_id: number | null
  urgent: boolean
}

interface Props {
  customerId: number
  data: LogFormData
  setData: (
    key: keyof LogFormData,
    value: string | number | boolean | null,
  ) => void
  errors?: Record<string, string[]>
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  submitLabel: string
  log_types: Option[]
  locations: Option[]
  contacts: Option[]
  users: Option[]
}

export default function LogForm({
  customerId,
  data,
  setData,
  errors,
  processing,
  onSubmit,
  submitLabel,
  log_types,
  locations,
  contacts,
  users,
}: Props) {
  return (
    <form onSubmit={onSubmit} className="max-w-3xl">
      <div className="mb-4">
        <label htmlFor="header" className="mb-1 block font-medium">
          Subject
        </label>
        <input
          type="text"
          id="header"
          value={data.header}
          onChange={(e) => setData("header", e.target.value)}
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
          maxLength={200}
        />
        {errors?.header && (
          <p className="text-hpi-red mt-1 text-sm">
            {errors.header.join(", ")}
          </p>
        )}
      </div>

      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="log_type_id" className="mb-1 block font-medium">
            Log Type
          </label>
          <select
            id="log_type_id"
            value={data.log_type_id ?? ""}
            onChange={(e) =>
              setData(
                "log_type_id",
                e.target.value ? parseInt(e.target.value) : null,
              )
            }
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          >
            <option value="">Select...</option>
            {log_types.map((lt) => (
              <option key={lt.id} value={lt.id}>
                {lt.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="user_id" className="mb-1 block font-medium">
            Assigned To
          </label>
          <select
            id="user_id"
            value={data.user_id ?? ""}
            onChange={(e) =>
              setData(
                "user_id",
                e.target.value ? parseInt(e.target.value) : null,
              )
            }
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          >
            <option value="">Select...</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <div>
          <label
            htmlFor="customer_location_id"
            className="mb-1 block font-medium"
          >
            Location
          </label>
          <select
            id="customer_location_id"
            value={data.customer_location_id ?? ""}
            onChange={(e) =>
              setData(
                "customer_location_id",
                e.target.value ? parseInt(e.target.value) : null,
              )
            }
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          >
            <option value="">Select...</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="customer_user_id" className="mb-1 block font-medium">
            Contact
          </label>
          <select
            id="customer_user_id"
            value={data.customer_user_id ?? ""}
            onChange={(e) =>
              setData(
                "customer_user_id",
                e.target.value ? parseInt(e.target.value) : null,
              )
            }
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          >
            <option value="">Select...</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="log_date" className="mb-1 block font-medium">
          Log Date
        </label>
        <input
          type="datetime-local"
          id="log_date"
          value={data.log_date ? data.log_date.slice(0, 16) : ""}
          onChange={(e) => setData("log_date", e.target.value)}
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
        />
      </div>

      <div className="mb-4">
        <label htmlFor="log" className="mb-1 block font-medium">
          Log Entry
        </label>
        <textarea
          id="log"
          value={data.log}
          onChange={(e) => setData("log", e.target.value)}
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
          rows={5}
        />
      </div>

      <div className="mb-4">
        <label htmlFor="notes" className="mb-1 block font-medium">
          Notes
        </label>
        <textarea
          id="notes"
          value={data.notes}
          onChange={(e) => setData("notes", e.target.value)}
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
          rows={3}
        />
      </div>

      <div className="mb-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={data.urgent}
            onChange={(e) => setData("urgent", e.target.checked)}
            className="border-hpi-light-gray rounded"
          />
          <span className="font-medium">Urgent</span>
        </label>
      </div>

      <div className="border-hpi-light-gray mb-6 rounded border p-4">
        <h3 className="mb-4 font-semibold">Schedule Follow-up (Optional)</h3>

        <div className="mb-4">
          <label htmlFor="scheduled_date" className="mb-1 block font-medium">
            Scheduled Date
          </label>
          <input
            type="datetime-local"
            id="scheduled_date"
            value={data.scheduled_date ? data.scheduled_date.slice(0, 16) : ""}
            onChange={(e) => setData("scheduled_date", e.target.value || null)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="scheduled_notes" className="mb-1 block font-medium">
            Scheduled Notes
          </label>
          <textarea
            id="scheduled_notes"
            value={data.scheduled_notes}
            onChange={(e) => setData("scheduled_notes", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
            rows={2}
          />
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <Link
          href={`/customers/${customerId}/logs`}
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
